import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  from,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import type { AuthApi } from "../shell-handle/auth";

function buildAuthLink(auth: AuthApi) {
  return setContext(async (_, prevContext) => {
    const { headers, forceRefresh } = prevContext as {
      headers?: Record<string, string>;
      forceRefresh?: boolean;
    };
    let token = "";
    try {
      token = await auth.getToken({ forceRefresh: !!forceRefresh });
    } catch {
      /* unauth, request may still proceed for /login flows */
    }
    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });
}

function buildErrorLink(auth: AuthApi) {
  return onError(({ networkError, operation, forward }) => {
    const status = (networkError as { statusCode?: number } | null | undefined)
      ?.statusCode;
    if (status !== 401 && status !== 403) return;
    const ctx = operation.getContext() as { didRetryAuth?: boolean };
    if (ctx.didRetryAuth) return;
    return new Observable((observer) => {
      void auth
        .getToken({ forceRefresh: true })
        .then(() => {
          operation.setContext({
            ...ctx,
            didRetryAuth: true,
            forceRefresh: true,
          });
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch((err) => observer.error(err));
    });
  });
}

export function createApolloClient(auth: AuthApi): ApolloClient<unknown> {
  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_NEMO_AUTH_GQL_URL,
  });
  return new ApolloClient({
    link: from([buildErrorLink(auth), buildAuthLink(auth), httpLink]),
    cache: new InMemoryCache(),
  });
}

export function createCloudApolloClient(auth: AuthApi): ApolloClient<unknown> {
  const httpLink = new HttpLink({ uri: import.meta.env.VITE_CLOUD_GQL_URL });
  return new ApolloClient({
    link: from([buildErrorLink(auth), buildAuthLink(auth), httpLink]),
    cache: new InMemoryCache({ addTypename: false }),
  });
}
